import React, { createContext, useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'

import Veiculo, {
  ITipoCaminhao,
} from 'modules/veiculos/domain/entities/Veiculo'

import ICaminhao from '../domain/entities/Caminhao'
import { ICreateCaminhao } from '../domain/usecases/CreateCaminhao'
import CaminhaoRoutes from '../utils/CaminhaoRoutes'

interface ICaminhaoContextState {
  caminhao: ICaminhao
  setCaminhao: (caminhao: ICaminhao) => void
  tipoCaminhao: ITipoCaminhao
  setTipoCaminhao: (tipoCaminhao: ITipoCaminhao) => void
  nomeCaminhao: string
  setNomeCaminhao: (nomeCaminhao: string) => void
  veiculo: Veiculo
  setVeiculo: (veiculo: Veiculo) => void
  carreta1: Veiculo
  setCarreta1: (veiculo: Veiculo) => void
  carreta2: Veiculo
  setCarreta2: (veiculo: Veiculo) => void
  carreta3: Veiculo
  setCarreta3: (veiculo: Veiculo) => void
  placaCarreta1Input: string
  setPlacaCarreta1Input: (value: string) => void
  placaCarreta2Input: string
  setPlacaCarreta2Input: (value: string) => void
  placaCarreta3Input: string
  setPlacaCarreta3Input: (value: string) => void
  step: number
  setStep: (value: number) => void
  numberOfSteps: number
  handleNextButton: () => void
  handleBackButton: () => void
  openModal: boolean
  setOpenModal: (value: boolean) => void
  error: boolean
  setError: (value: boolean) => void
  loading: boolean
  setLoading: (value: boolean) => void
  errorMessage: string
  setErrorMessage: (value: string) => void
  setCreateCaminhao: (createCaminhao: ICreateCaminhao) => void
  handleCreateCaminhao: (
    frotistaId: number,
    nomeCaminhao: string,
    tipoCaminhaoId: number,
    veiculoCavaloId: number,
    veiculoCarreta1Id?: number,
    veiculoCarreta2Id?: number,
    veiculoCarreta3Id?: number,
  ) => Promise<void>
  indiceCarreta: number
  setIndiceCarreta: (value: number) => void
  clearCaminhao(): void
  openModalSelectFrotista: boolean
  setOpenModalSelectFrotista: (value: boolean) => void
}

const CaminhaoContext = createContext<ICaminhaoContextState>(
  {} as ICaminhaoContextState,
)

export function useCaminhao(): ICaminhaoContextState {
  const context = useContext(CaminhaoContext)

  if (!context) {
    throw new Error('useCaminhao must be used within an CaminhaoProvider')
  }

  return context
}

export const CaminhaoProvider: React.FC = ({ children }) => {
  const history = useHistory()
  const [caminhao, setCaminhao] = useState<ICaminhao>({})
  const [tipoCaminhao, setTipoCaminhao] = useState<ITipoCaminhao>({})
  const [nomeCaminhao, setNomeCaminhao] = useState<string>('')
  const [veiculo, setVeiculo] = useState<Veiculo>(null)
  const [carreta1, setCarreta1] = useState<Veiculo>(null)
  const [carreta2, setCarreta2] = useState<Veiculo>(null)
  const [carreta3, setCarreta3] = useState<Veiculo>(null)
  const [placaCarreta1Input, setPlacaCarreta1Input] = useState(null)
  const [placaCarreta2Input, setPlacaCarreta2Input] = useState(null)
  const [placaCarreta3Input, setPlacaCarreta3Input] = useState(null)
  const [step, setStep] = useState(0)
  const [openModal, setOpenModal] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [createCaminhao, setCreateCaminhao] = useState<ICreateCaminhao>()
  const [indiceCarreta, setIndiceCarreta] = useState(1)
  const [openModalSelectFrotista, setOpenModalSelectFrotista] = useState(false)

  const clearContext = () => {
    setVeiculo(null)
    setNomeCaminhao(null)
    setTipoCaminhao(null)
    setCarreta1(null)
    setCarreta2(null)
    setCarreta3(null)
    setPlacaCarreta1Input(null)
    setPlacaCarreta2Input(null)
    setPlacaCarreta3Input(null)
    setStep(0)
    setIndiceCarreta(1)
  }
  const setCaminhaoValue = (value: ICaminhao) => {
    setCaminhao(value)
  }

  const setTipoCaminhaoValue = (value: ITipoCaminhao) => {
    setTipoCaminhao(value)
  }

  const setNomeCaminhaoValue = (value: string) => {
    setNomeCaminhao(value)
  }

  const setVeiculoValue = (value: Veiculo) => {
    setVeiculo(value)
  }

  const setCarreta1Value = (value: Veiculo) => {
    setCarreta1(value)
  }

  const setCarreta2Value = (value: Veiculo) => {
    setCarreta2(value)
  }

  const setCarreta3Value = (value: Veiculo) => {
    setCarreta3(value)
  }

  const setPlacaCarreta1InputValue = (value: string) => {
    setPlacaCarreta1Input(value)
  }

  const setPlacaCarreta2InputValue = (value: string) => {
    setPlacaCarreta2Input(value)
  }

  const setPlacaCarreta3InputValue = (value: string) => {
    setPlacaCarreta3Input(value)
  }

  const setStepValue = (value: number) => {
    setStep(value)
  }

  const setOpenModalSelectFrotistaValue = (value: boolean) => {
    setOpenModalSelectFrotista(value)
  }

  const setErrorValue = (value: boolean) => {
    setError(value)
  }

  const setLoadingValue = (value: boolean) => {
    setLoading(value)
  }

  const setErrorMessageValue = (value: string) => {
    setErrorMessage(value)
  }

  const setCreateCaminhaoValue = (value: ICreateCaminhao) => {
    setCreateCaminhao(value)
  }

  const setIndiceCarretaValue = (value: number) => {
    setIndiceCarreta(value)
  }

  const setOpenModalValue = (value: boolean) => {
    setOpenModal(value)
  }

  const numberOfSteps =
    Object.keys({
      Nome: 0,
      Cavalo: 1,
      Carreta1: 2,
      Carreta2: 3,
      Carreta3: 4,
    }).length - 1

  const handleNextButtonValue = () => {
    setStep(step => {
      const newStep = step + 1

      if (newStep > numberOfSteps) {
        return step
      }

      return newStep
    })
  }

  const handleBackButtonValue = () => {
    setStep(step => {
      const newStep = step - 1

      if (step > 2) {
        setIndiceCarreta(indiceCarreta - 1)
      }

      if (newStep < 0) {
        clearContext()
        history.push(CaminhaoRoutes.LIST)
        return 0
      }

      return step - 1
    })
  }

  const handleCreateCaminhaoFn = async (
    frotistaId: number,
    nomeCaminhao: string,
    tipoCaminhaoId: number,
    veiculoCavaloId: number,
    veiculoCarreta1Id: number,
    veiculoCarreta2Id: number,
    veiculoCarreta3Id: number,
  ) => {
    if (!frotistaId) {
      setOpenModalSelectFrotista(true)
      return
    }
    setLoadingValue(true)

    try {
      const response = await createCaminhao.execute({
        frotista_id: frotistaId,
        descricao: nomeCaminhao,
        tipo_caminhao_id: tipoCaminhaoId,
        veiculo_cavalo_id: veiculoCavaloId,
        veiculo_carreta1_id: veiculoCarreta1Id,
        veiculo_carreta2_id: veiculoCarreta2Id,
        veiculo_carreta3_id: veiculoCarreta3Id,
      })

      if (response) {
        setOpenModalValue(true)
        setLoadingValue(false)
        clearContext()
      }
    } catch (e: unknown) {
      setOpenModalValue(false)
      setLoadingValue(false)
      if (e instanceof Error) {
        setErrorMessage(e.message)
      } else {
        setErrorMessage('Erro ao tentar criar o caminhão')
      }
      setError(true)
    }
  }

  return (
    <CaminhaoContext.Provider
      value={{
        caminhao,
        setCaminhao: setCaminhaoValue,
        tipoCaminhao,
        setTipoCaminhao: setTipoCaminhaoValue,
        nomeCaminhao,
        setNomeCaminhao: setNomeCaminhaoValue,
        veiculo,
        setVeiculo: setVeiculoValue,
        carreta1,
        setCarreta1: setCarreta1Value,
        carreta2,
        setCarreta2: setCarreta2Value,
        carreta3,
        setCarreta3: setCarreta3Value,
        placaCarreta1Input,
        setPlacaCarreta1Input: setPlacaCarreta1InputValue,
        placaCarreta2Input,
        setPlacaCarreta2Input: setPlacaCarreta2InputValue,
        placaCarreta3Input,
        setPlacaCarreta3Input: setPlacaCarreta3InputValue,
        step,
        setStep: setStepValue,
        numberOfSteps,
        handleNextButton: handleNextButtonValue,
        handleBackButton: handleBackButtonValue,
        openModal,
        setOpenModal: setOpenModalValue,
        error,
        setError: setErrorValue,
        loading,
        setLoading: setLoadingValue,
        errorMessage,
        setErrorMessage: setErrorMessageValue,
        setCreateCaminhao: setCreateCaminhaoValue,
        handleCreateCaminhao: handleCreateCaminhaoFn,
        indiceCarreta,
        setIndiceCarreta: setIndiceCarretaValue,
        clearCaminhao: clearContext,
        setOpenModalSelectFrotista: setOpenModalSelectFrotistaValue,
        openModalSelectFrotista,
      }}
    >
      {children}
    </CaminhaoContext.Provider>
  )
}
